/**
 * PixiJS v8 renderer. Owns the WebGL/WebGPU surface and draws four layers:
 * mirrored camera video, falling food, particle FX, and mouth-feedback UI.
 *
 * "Field space" ([0,1]) is the shared coordinate system with the sim. The
 * camera is cover-fitted and horizontally mirrored; `videoNormToField` maps
 * raw (unmirrored) MediaPipe coordinates through that same transform so the
 * ellipse the sim sees lines up with what the player sees.
 */

import { Application, Assets, Container, Graphics, Sprite, Texture } from "pixi.js";
import type { Ticker } from "pixi.js";
import type { MouthSample, RoundState } from "./types.js";
import { FOODS } from "./types.js";

interface CoverMap {
  scale: number;
  offX: number;
  offY: number;
  vw: number;
  vh: number;
  sw: number;
  sh: number;
}

export class GameRenderer {
  app!: Application;

  private readonly videoLayer = new Container();
  private readonly foodLayer = new Container();
  private readonly fxLayer = new Container();
  private readonly uiLayer = new Container();
  private readonly mouthG = new Graphics();

  private videoSprite: Sprite | null = null;
  private video: HTMLVideoElement | null = null;
  private readonly textures = new Map<string, Texture>();
  private readonly foodSprites = new Map<number, Sprite>();

  private debug = false;
  private map: CoverMap = { scale: 1, offX: 0, offY: 0, vw: 1, vh: 1, sw: 1, sh: 1 };

  async init(mount: HTMLElement): Promise<void> {
    // Defensive: init() must only ever stand up one Application/canvas. A
    // caller retrying its own setup (e.g. after a failed camera step) must
    // not be able to double-init this renderer.
    if (this.app) return;
    this.debug = new URLSearchParams(location.search).has("debug");
    const app = new Application();
    await app.init({
      resizeTo: mount,
      background: "#101014",
      resolution: Math.min(window.devicePixelRatio, 1.5),
      autoDensity: true,
    });
    mount.appendChild(app.canvas as HTMLCanvasElement);
    this.app = app;
    app.stage.addChild(this.videoLayer, this.foodLayer, this.fxLayer, this.uiLayer);
    this.uiLayer.addChild(this.mouthG);
  }

  async loadFoodTextures(): Promise<void> {
    await Promise.all(
      FOODS.map(async (f) => {
        const tex = (await Assets.load(`/assets/food/${f.kind}.png`)) as Texture;
        this.textures.set(f.kind, tex);
      }),
    );
  }

  attachVideo(video: HTMLVideoElement): void {
    this.video = video;
    const sprite = new Sprite(Texture.from(video));
    this.videoSprite = sprite;
    this.videoLayer.addChild(sprite);
  }

  /** Destroys pooled food sprites and clears the mouth outline. Call between rounds. */
  clearFoods(): void {
    for (const sprite of this.foodSprites.values()) sprite.destroy();
    this.foodSprites.clear();
    this.mouthG.clear();
  }

  /** Cheap; call every frame. Recomputes the mirrored cover-fit transform. */
  layout(): void {
    const screen = this.app.renderer.screen;
    const sw = screen.width;
    const sh = screen.height;
    const v = this.video;
    // Read live dimensions each frame — iOS can swap them mid-stream.
    const vw = v && v.videoWidth > 0 ? v.videoWidth : sw;
    const vh = v && v.videoHeight > 0 ? v.videoHeight : sh;
    const scale = Math.max(sw / vw, sh / vh);
    const dispW = vw * scale;
    const dispH = vh * scale;
    const offX = (sw - dispW) / 2;
    const offY = (sh - dispH) / 2;
    this.map = { scale, offX, offY, vw, vh, sw, sh };

    if (this.videoSprite) {
      // Negative x scale mirrors horizontally; with the default top-left anchor
      // the image then occupies [x - dispW, x], so x = offX + dispW to cover.
      this.videoSprite.scale.set(-scale, scale);
      this.videoSprite.x = offX + dispW;
      this.videoSprite.y = offY;
    }
  }

  /** Map unmirrored normalized-video coords → mirrored, cover-fit field [0,1]. */
  videoNormToField(vx: number, vy: number): { x: number; y: number } {
    const m = this.map;
    const dispW = m.vw * m.scale;
    const dispH = m.vh * m.scale;
    const screenX = m.offX + dispW * (1 - vx);
    const screenY = m.offY + dispH * vy;
    return { x: screenX / m.sw, y: screenY / m.sh };
  }

  render(state: RoundState, mouth: MouthSample | null, msSinceTick: number): void {
    const sw = this.map.sw;
    const sh = this.map.sh;

    const seen = new Set<number>();
    for (const food of state.foods) {
      seen.add(food.id);
      let sprite = this.foodSprites.get(food.id);
      if (!sprite) {
        const tex = this.textures.get(food.kind) ?? Texture.EMPTY;
        sprite = new Sprite(tex);
        sprite.anchor.set(0.5);
        this.foodSprites.set(food.id, sprite);
        this.foodLayer.addChild(sprite);
      }
      const tw = sprite.texture.width || 1;
      const th = sprite.texture.height || 1;
      const w = food.size * sw;
      sprite.width = w;
      sprite.height = w * (th / tw);
      sprite.x = food.x * sw;
      // Interpolate the fall between fixed ticks for smooth rendering.
      sprite.y = (food.y + food.vy * (msSinceTick / 1000)) * sh;
    }
    for (const [id, sprite] of this.foodSprites) {
      if (!seen.has(id)) {
        sprite.destroy();
        this.foodSprites.delete(id);
      }
    }

    this.mouthG.clear();
    if (mouth && mouth.open) {
      const cx = mouth.cx * sw;
      const cy = mouth.cy * sh;
      const rx = mouth.rx * sw;
      const ry = mouth.ry * sh;
      this.mouthG.ellipse(cx, cy, rx, ry).stroke({ width: 3, color: 0xffffff, alpha: 0.85 });
      if (this.debug) {
        this.mouthG.ellipse(cx, cy, rx, ry).fill({ color: 0xffffff, alpha: 0.2 });
      }
    }
  }

  /** Particle burst at field-space coords: 8 circles radiating, fade + shrink. */
  burst(fx: number, fy: number, color: number): void {
    const cx = fx * this.map.sw;
    const cy = fy * this.map.sh;
    const reach = Math.min(this.map.sw, this.map.sh) * 0.07;
    const g = new Graphics();
    this.fxLayer.addChild(g);
    const life = 500;
    let elapsed = 0;
    const step = (ticker: Ticker) => {
      elapsed += ticker.deltaMS;
      const t = Math.min(elapsed / life, 1);
      const alpha = 1 - t;
      const rad = 6 * (1 - t) + 1.5;
      g.clear();
      for (let i = 0; i < 8; i++) {
        const ang = (Math.PI * 2 * i) / 8;
        const px = cx + Math.cos(ang) * reach * t;
        const py = cy + Math.sin(ang) * reach * t;
        g.circle(px, py, rad).fill({ color, alpha });
      }
      if (t >= 1) {
        this.app.ticker.remove(step);
        g.destroy();
      }
    };
    this.app.ticker.add(step);
  }

  /** Full-screen fading color overlay. */
  flash(color: number, alpha: number, ms: number): void {
    const g = new Graphics();
    this.uiLayer.addChild(g);
    let elapsed = 0;
    const step = (ticker: Ticker) => {
      elapsed += ticker.deltaMS;
      const t = Math.min(elapsed / ms, 1);
      g.clear();
      g.rect(0, 0, this.map.sw, this.map.sh).fill({ color, alpha: alpha * (1 - t) });
      if (t >= 1) {
        this.app.ticker.remove(step);
        g.destroy();
      }
    };
    this.app.ticker.add(step);
  }
}
